import { createSelector } from 'reselect';

export const selectPaymentPlansState = (state) => state.paymentPlans;

export const selectSelectedPaymentPlanId = createSelector(
  selectPaymentPlansState,
  (paymentPlansState) => paymentPlansState.selectedPaymentPlanId,
);

export const selectPaymentPlans = createSelector(
  selectPaymentPlansState,
  (paymentPlansState) => paymentPlansState.paymentPlans,
);

export const selectPaymentPlansDynamicData = createSelector(
  selectPaymentPlansState,
  (paymentPlansState) => paymentPlansState.paymentPlansDynamicData,
);

// Eligibility is determined solely by fetchPaymentPlans (paymentPlansDynamicData); only plans
// the server returns there should be shown to the user.
export const selectEligiblePaymentPlans = createSelector(
  selectPaymentPlans,
  selectPaymentPlansDynamicData,
  (paymentPlans, paymentPlansDynamicData) => {
    const eligiblePlanIds = new Set(paymentPlansDynamicData.map((data) => Number(data.paymentPlanId)));
    return paymentPlans.filter((plan) => eligiblePlanIds.has(Number(plan.paymentPlanId)));
  },
);

export const selectFullPaymentPlansData = createSelector(
  selectEligiblePaymentPlans,
  selectPaymentPlansDynamicData,
  (paymentPlans, paymentPlansDynamicData) => {
    const dynamicDataByPlanId = paymentPlansDynamicData.reduce((acc, data) => {
      acc[data.paymentPlanId] = data;
      return acc;
    }, {});

    return paymentPlans.map((plan) => ({
      ...plan,
      ...dynamicDataByPlanId[plan.paymentPlanId],
    }));
  },
);

export const selectSelectedPaymentPlan = createSelector(
  selectSelectedPaymentPlanId,
  selectFullPaymentPlansData,
  (selectedPaymentPlanId, paymentPlans) => {
    if (selectedPaymentPlanId == null || selectedPaymentPlanId === -1) {
      return null;
    }
    return paymentPlans.find((plan) => Number(plan.paymentPlanId) === Number(selectedPaymentPlanId)) ?? null;
  },
);

/** Minor-unit amount to charge at checkout (initial installment when a plan is selected, else order total). */
export const selectPaymentChargeMinorAmount = createSelector(
  (state) => state.reservation.total_cost?.amount || 0,
  selectSelectedPaymentPlanId,
  selectFullPaymentPlansData,
  (totalAmount, selectedPaymentPlanId, plans) => {
    if (selectedPaymentPlanId == null || selectedPaymentPlanId === -1) {
      return totalAmount;
    }
    const plan = plans.find((p) => Number(p.paymentPlanId) === Number(selectedPaymentPlanId));
    if (plan?.initialInstallmentAmount?.amount != null) {
      return plan.initialInstallmentAmount.amount;
    }
    return totalAmount;
  },
);

/** Minor-unit amount for Stripe PaymentIntent create/update. */
export const selectStripePaymentIntentMinorAmount = selectPaymentChargeMinorAmount;
